import { useMemo } from 'react';

import { useAuthStore } from '~/store/auth.store';
import { getTokenSubsidiaryId } from '~/utils/access-token';

import type { ExportTenantContext } from '../types/export.types';

/**
 * Resolve the `{ subsidiaryId, organizationId, subsidiaryCode }` triple the
 * export API requires. Returns `null` when no subsidiary can be resolved or its
 * slug/parent is unknown, so callers can gate the trigger button.
 *
 * The subsidiary is the one the request's `X-Subsidiary-ID` header carries —
 * the access token's own claim, else the active tenant (`subsidiaryInterceptor`).
 * The service checks the body against the subsidiary it resolves from that
 * header and answers a mismatch with a 404 ("Source enrichment job not found"),
 * so the body must never name a different one: using `activeTenantId` alone
 * broke every export read whenever it differed from the token's claim.
 * `organizationId` is the subsidiary's parent (falling back to its root tenant).
 */
export function useExportTenantContext(): ExportTenantContext | null {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);

  return useMemo(() => {
    if (!user) return null;
    const subsidiaryId = getTokenSubsidiaryId(accessToken) ?? user.activeTenantId;
    const tenant = user.accessibleTenants.find((t) => t.id === subsidiaryId);
    if (!tenant) return null;
    const organizationId = tenant.parentId ?? tenant.rootTenantId;
    if (!organizationId || !tenant.slug) return null;
    return { subsidiaryId, organizationId, subsidiaryCode: tenant.slug };
  }, [user, accessToken]);
}
